import { config } from "../config";
import { commonHeaders } from "./client";
import { toApiError } from "./errors";

/** One parsed Server-Sent Event. `data` is JSON-decoded. */
export interface SSEMessage<T = unknown> {
  id?: string;
  event: string;
  data: T;
}

export interface StreamOptions<T> {
  /** Called for every event (including heartbeats). */
  onMessage: (msg: SSEMessage<T>) => void;
  onOpen?: () => void;
  onError?: (err: unknown) => void;
  /** Abort to close the stream. */
  signal: AbortSignal;
  /** Resume after this event id (sent as Last-Event-ID). */
  lastEventId?: string;
  /** Reconnect after a dropped connection (default true). Stops when `isTerminal` returns true. */
  reconnect?: boolean;
  /** Return true for the event that ends the stream, e.g. operation.finished. */
  isTerminal?: (msg: SSEMessage<T>) => boolean;
}

/**
 * Stream Server-Sent Events with fetch (not EventSource) so we can send Guv headers and resume
 * with Last-Event-ID. Resolves when the stream ends for good or the signal aborts.
 *
 *   const ac = new AbortController();
 *   streamSSE<OperationEvent>(`/v1/operations/${id}/events`, { signal: ac.signal, onMessage: (m) => ... });
 */
export async function streamSSE<T = unknown>(path: string, opts: StreamOptions<T>): Promise<void> {
  const { signal, reconnect = true } = opts;
  let lastId = opts.lastEventId;
  let attempt = 0;
  const base = config.apiUrl || window.location.origin;

  while (!signal.aborted) {
    try {
      const headers: Record<string, string> = { ...commonHeaders(), Accept: "text/event-stream" };
      if (lastId) headers["Last-Event-ID"] = lastId;
      const res = await fetch(`${base}${path}`, { headers, credentials: "include", signal });
      if (!res.ok || !res.body) {
        const body = await res.json().catch(() => undefined);
        throw toApiError(body, res);
      }
      attempt = 0;
      opts.onOpen?.();
      let finished = false;
      for await (const msg of parseSSE<T>(res.body)) {
        if (msg.id) lastId = msg.id;
        opts.onMessage(msg);
        if (opts.isTerminal?.(msg)) {
          finished = true;
          break;
        }
      }
      if (finished || !reconnect) return;
    } catch (err) {
      if (signal.aborted) return;
      opts.onError?.(err);
      if (!reconnect) return;
      // Permanent errors: do not hammer the API.
      const status = (err as { status?: number }).status;
      if (status && status >= 400 && status < 500 && status !== 429) return;
    }
    attempt++;
    const wait = Math.min(15_000, 500 * 2 ** attempt);
    await sleep(wait, signal);
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(t);
      resolve();
    }, { once: true });
  });
}

/** Parse a text/event-stream body into messages. */
export async function* parseSSE<T>(body: ReadableStream<Uint8Array>): AsyncGenerator<SSEMessage<T>> {
  const reader = body.pipeThrough(new TextDecoderStream() as unknown as ReadableWritablePair<string, Uint8Array>).getReader();
  let buf = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += value;
      let idx: number;
      while ((idx = buf.search(/\r?\n\r?\n/)) !== -1) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx).replace(/^\r?\n\r?\n/, "");
        const msg = parseChunk<T>(chunk);
        if (msg) yield msg;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseChunk<T>(chunk: string): SSEMessage<T> | null {
  let event = "message";
  let id: string | undefined;
  const data: string[] = [];
  for (const line of chunk.split(/\r?\n/)) {
    if (!line || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? "" : line.slice(colon + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
    else if (field === "id") id = value;
  }
  if (data.length === 0 && event === "message") return null;
  const raw = data.join("\n");
  let parsed: unknown = raw;
  try {
    parsed = raw ? JSON.parse(raw) : undefined;
  } catch {
    /* keep raw string */
  }
  return { id, event, data: parsed as T };
}
